/*
 * Copyright 2023 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { createTranslationRef } from '@backstage/core-plugin-api/alpha';

/** @public */
export const catalogTranslationRef = createTranslationRef({
  id: 'catalog',
  messages: {
    indexPage: {
      title: `{{orgName}} Catalog`,
      createButtonTitle: 'Create',
      supportButtonContent: 'All your software catalog entities',
    },
    entityPage: {
      notFoundMessage: 'There is no {{kind}} with the requested {{link}}.',
      notFoundLinkText: 'kind, namespace, and name',
    },
    aboutCard: {
      title: 'About',
      refreshButtonTitle: 'Schedule entity refresh',
      editButtonTitle: 'Edit Metadata',
      editButtonAriaLabel: 'Edit',
      createSimilarButtonTitle: 'Create something similar',
      refreshScheduledMessage: 'Refresh scheduled',
      refreshButtonAriaLabel: 'Refresh',
      launchTemplate: 'Launch Template',
      viewTechdocs: 'View TechDocs',
      viewSource: 'View Source',
      unknown: 'unknown',
      descriptionField: {
        label: 'Description',
        value: 'No description',
      },
      ownerField: {
        label: 'Owner',
        value: 'No Owner',
      },
      domainField: {
        label: 'Domain',
        value: 'No Domain',
      },
      systemField: {
        label: 'System',
        value: 'No System',
      },
      parentComponentField: {
        label: 'Parent Component',
        value: 'No Parent Component',
      },
      kindField: {
        label: 'Kind',
      },
      typeField: {
        label: 'Type',
      },
      lifecycleField: {
        label: 'Lifecycle',
      },
      tagsField: {
        label: 'Tags',
        value: 'No Tags',
      },
      targetsField: {
        label: 'Targets',
      },
    },
    searchResultItem: {
      kind: 'Kind',
      type: 'Type',
      lifecycle: 'Lifecycle',
      owner: 'Owner',
    },
    catalogTable: {
      warningPanelTitle: 'Could not fetch catalog entities.',
      viewActionTitle: 'View',
      editActionTitle: 'Edit',
      starActionTitle: 'Add to favorites',
      unStarActionTitle: 'Remove from favorites',
      allFilters: 'All',
    },
    dependencyOfComponentsCard: {
      title: 'Dependency of components',
      emptyMessage: 'No component depends on this component.',
    },
    dependsOnComponentsCard: {
      title: 'Depends on components',
      emptyMessage: 'No component is a dependency of this component.',
    },
    dependsOnResourcesCard: {
      title: 'Depends on resources',
      emptyMessage: 'No resource is a dependency of this component.',
    },
    entityContextMenu: {
      copiedMessage: 'Copied!',
      moreButtonTitle: 'More',
      inspectMenuTitle: 'Inspect entity',
      copyURLMenuTitle: 'Copy entity URL',
      unregisterMenuTitle: 'Unregister entity',
      moreButtonAriaLabel: 'More actions',
    },
    entityLabelsCard: {
      title: 'Labels',
      columnKeyLabel: 'Label',
      columnValueLabel: 'Value',
      emptyDescription:
        'No labels defined for this entity. You can add labels to your entity YAML as shown in the highlighted example below:',
      readMoreButtonTitle: 'Read more',
    },
    entityLabels: {
      warningPanelTitle: 'Entity not found',
      ownerLabel: 'Owner',
      lifecycleLabel: 'Lifecycle',
      systemLabel: 'System',
      domainLabel: 'Domain',
      partOfLabel: 'Part of',
    },
    entityLinksCard: {
      title: 'Links',
      emptyDescription:
        'No links defined for this entity. You can add links to your entity YAML as shown in the highlighted example below:',
      readMoreButtonTitle: 'Read more',
    },
    entityNotFound: {
      title: 'Entity was not found',
      description:
        'Want to help us build this? Check out our Getting Started documentation.',
      docButtonTitle: 'DOCS',
    },
    entityTabs: {
      tabsAriaLabel: 'Tabs',
    },
    deleteEntity: {
      dialogTitle: 'Are you sure you want to delete this entity?',
      deleteButtonTitle: 'Delete',
      cancelButtonTitle: 'Cancel',
      description:
        'This entity is not referenced by any location and is therefore not receiving updates.',
      actionButtonTitle: 'Delete entity',
    },
    catalogExportButton: {
      triggerButtonTitle: 'Export selection',
      dialogTitle: 'Export catalog selection',
      formatLabel: 'Format',
      columnsLabel: 'Columns',
      cancelButtonTitle: 'Cancel',
      confirmButtonTitle: 'Confirm',
      exportingButtonTitle: 'Exporting…',
      successMessage: 'Catalog exported successfully',
      errorMessage: 'Failed to export catalog: {{errorMessage}}',
    },
    entityProcessingErrorsDescription: 'The error below originates from',
    entityRelationWarningDescription:
      "This entity has relations to other entities, which can't be found in the catalog.\n Entities not found are: ",
    hasComponentsCard: {
      title: 'Has components',
      emptyMessage: 'No component is part of this system.',
    },
    hasResourcesCard: {
      title: 'Has resources',
      emptyMessage: 'No resource is part of this system.',
    },
    hasSubcomponentsCard: {
      title: 'Has subcomponents',
      emptyMessage: 'No subcomponent is part of this component.',
    },
    hasSubdomainsCard: {
      title: 'Has subdomains',
      emptyMessage: 'No subdomain is part of this domain.',
    },
    hasSystemsCard: {
      title: 'Has systems',
      emptyMessage: 'No system is part of this domain.',
    },
    relatedEntitiesCard: {
      emptyHelpLinkTitle: 'Learn how to change this.',
    },
    systemDiagramCard: {
      title: 'System Diagram',
      description: 'Use pinch & zoom to move around the diagram.',
      edgeLabels: {
        partOf: 'part of',
        provides: 'provides',
        dependsOn: 'depends on',
      },
    },
  },
});
